import { useCallback, useEffect, useRef, useState } from "react";

type SaveFn<T> = (patch: T) => Promise<void>;

export type AutosaveStatus = "idle" | "saving" | "saved" | "error";

export function useAutosave<T extends object>(save: SaveFn<T>, debounceMs = 700) {
  const saveRef = useRef(save);
  useEffect(() => { saveRef.current = save; }, [save]);

  const [status, setStatus] = useState<AutosaveStatus>("idle");
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const pendingPatch = useRef<Partial<T>>({});
  const timer = useRef<number | null>(null);

  const flush = useCallback(async () => {
    const patch = pendingPatch.current;
    if (!patch || Object.keys(patch).length === 0) return;
    pendingPatch.current = {};
    setStatus("saving");
    try {
      await saveRef.current(patch as T);
      setStatus("saved");
      setLastSavedAt(Date.now());
    } catch {
      setStatus("error");
    }
  }, []); // no deps — saveRef stays current via the effect

  const notifyChange = useCallback(
    (patch: Partial<T>) => {
      pendingPatch.current = { ...pendingPatch.current, ...patch };
      setStatus("saving");
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => void flush(), debounceMs);
    },
    [debounceMs, flush],
  );

  useEffect(() => {
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, []);

  return {
    status,
    lastSavedAt,
    notifyChange,
    flush,
    isPending: status === "saving",
  };
}
